/**
 * UTILITAIRES CSV IMPORT
 * Fonctions pour l'import CSV intelligent avec validation et transformation
 */

import Papa from 'papaparse';
import { compareTwoStrings } from 'string-similarity';
import type {
  CSVColumnMapping,
  CSVImportConfig,
  CSVImportMode,
  CSVImportResult,
  CSVParsedData,
  CSVValidationError,
} from '../types/csv';
import { CSV_FORMULA_PATTERN, unescapeCSVFormula } from './csv';

/**
 * Parse un fichier CSV et retourne les données structurées
 */
export function parseCSVFile(file: File): Promise<CSVParsedData> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      delimiter: '', // Auto-détection
      skipEmptyLines: true,
      encoding: 'UTF-8',
      // Retire l'apostrophe anti-formule de exportToCSV, avant aperçu, validation et import
      transform: (value) => unescapeCSVFormula(value),
      complete: (results) => {
        try {
          const headers = results.meta.fields || [];
          const rows = results.data as Record<string, string>[];

          resolve({
            headers,
            rows,
            rowCount: rows.length,
          });
        } catch (error) {
          reject(error);
        }
      },
      error: (error) => {
        reject(error);
      },
    });
  });
}

/**
 * Normalise une chaîne pour la comparaison (retire accents, minuscules)
 */
export function normalizeString(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Retire les accents
    .replace(/[_-]/g, ' ') // Remplace _ et - par espace
    .trim();
}

/**
 * Génère un slug à partir d'un nom
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Retire les accents
    .replace(/[^a-z0-9]+/g, '-') // Remplace les caractères non-alphanumériques par des tirets
    .replace(/^-+|-+$/g, '') // Retire les tirets en début et fin
    .slice(0, 100); // Limite à 100 caractères
}

/**
 * Parse une erreur Supabase pour retourner un message clair en français
 */
function parseSupabaseError(error: any): string {
  // Si c'est une string, la retourner directement
  if (typeof error === 'string') {
    return error;
  }

  // Si c'est un objet (Error ou PostgrestError), vérifier d'abord les propriétés Supabase
  if (error && typeof error === 'object') {
    // Propriétés que useCRUD attache à l'erreur
    const code = (error as any).code;
    const message = error.message || (error as any).message;

    // Duplication de clé unique (code 23505)
    if (code === '23505' || message?.includes('23505') || message?.includes('duplicate key')) {
      // Extraire le nom de la contrainte depuis le message
      const constraintMatch = message?.match(/"([^"]+)"/);
      const constraint = constraintMatch ? constraintMatch[1] : 'clé unique';

      // Extraire le nom du champ depuis la contrainte (ex: products_slug_key → slug)
      const fieldMatch = constraint.match(/_([^_]+)_key$/);
      const field = fieldMatch ? fieldMatch[1] : constraint;

      return `Doublon : un élément avec ce "${field}" existe déjà`;
    }

    // Contrainte NOT NULL violée (code 23502)
    if (code === '23502' || message?.includes('23502') || message?.includes('null value')) {
      const columnMatch = message?.match(/column "([^"]+)"/);
      const column = columnMatch ? columnMatch[1] : 'champ requis';
      return `Champ obligatoire manquant : ${column}`;
    }

    // Contrainte de clé étrangère violée (code 23503)
    if (code === '23503' || message?.includes('23503') || message?.includes('foreign key')) {
      return "Relation invalide : la valeur référencée n'existe pas";
    }

    // Contrainte de vérification violée (code 23514)
    if (code === '23514' || message?.includes('23514') || message?.includes('check constraint')) {
      return 'Valeur invalide : ne respecte pas les contraintes de validation';
    }

    // Violation de contrainte d'unicité (autre pattern)
    if (message?.includes('unique constraint')) {
      return 'Doublon : cette valeur doit être unique';
    }

    // Retourner le message nettoyé (retire le préfixe [create])
    if (message) {
      return message.replace(/^\[create\]\s*/, '').replace(/^\[update\]\s*/, '');
    }

    // Fallback basé sur le code uniquement
    switch (code) {
      case '23505':
        return 'Doublon : cette valeur existe déjà';
      case '23502':
        return 'Champ obligatoire manquant';
      case '23503':
        return "Relation invalide : la valeur référencée n'existe pas";
      case '23514':
        return 'Valeur invalide : ne respecte pas les contraintes de validation';
    }
  }

  return 'Erreur lors de la création';
}

/**
 * Suggère automatiquement un mapping entre colonnes CSV et champs BDD
 * Utilise string-similarity avec un seuil de 0.6
 */
export function suggestColumnMapping<T>(
  csvHeaders: string[],
  config: CSVImportConfig<T>
): CSVColumnMapping[] {
  const allFields = [...config.requiredFields, ...config.optionalFields];
  const fieldLabels = config.fieldLabels;

  return csvHeaders.map((csvColumn) => {
    const normalizedCsvColumn = normalizeString(csvColumn);

    // Chercher la meilleure correspondance
    let bestMatch: string | null = null;
    let bestScore = 0;

    allFields.forEach((field) => {
      const fieldStr = String(field);
      const label = fieldLabels[field] || fieldStr;

      // Comparer avec le nom du champ
      const fieldScore = compareTwoStrings(normalizedCsvColumn, normalizeString(fieldStr));

      // Comparer avec le label
      const labelScore = compareTwoStrings(normalizedCsvColumn, normalizeString(label));

      const score = Math.max(fieldScore, labelScore);

      if (score > bestScore && score >= 0.6) {
        bestScore = score;
        bestMatch = fieldStr;
      }
    });

    // Générer des suggestions alternatives
    const suggestions: string[] = [];
    if (bestMatch) {
      suggestions.push(bestMatch);
    }

    // Ajouter d'autres suggestions avec score > 0.4
    allFields.forEach((field) => {
      const fieldStr = String(field);
      if (fieldStr !== bestMatch) {
        const label = fieldLabels[field] || fieldStr;
        const score = Math.max(
          compareTwoStrings(normalizedCsvColumn, normalizeString(fieldStr)),
          compareTwoStrings(normalizedCsvColumn, normalizeString(label))
        );
        if (score > 0.4) {
          suggestions.push(fieldStr);
        }
      }
    });

    return {
      csvColumn,
      dbField: bestMatch,
      isRequired: bestMatch ? config.requiredFields.includes(bestMatch as keyof T) : false,
      suggestions: suggestions.slice(0, 3),
      preview: [],
    };
  });
}

/**
 * Parse une date française (DD/MM/YYYY) vers format ISO (YYYY-MM-DD)
 */
export function parseFrenchDate(value: string): string | null {
  if (!value || typeof value !== 'string') return null;

  // Gère DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY
  const match = value.trim().match(/^(\d{1,2})[\\/.-](\d{1,2})[\\/.-](\d{4})$/);
  if (!match) return null;

  const [, day, month, year] = match;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

/**
 * Parse un booléen (supporte français et anglais)
 */
export function parseBoolean(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  const lower = value.toLowerCase().trim();
  return ['oui', 'yes', 'true', '1', 'vrai', 'o', 'y'].includes(lower);
}

/**
 * Parse un nombre (gère virgule décimale française)
 */
export function parseNumber(value: string): number | null {
  if (!value || typeof value !== 'string') return null;
  const cleaned = value
    .trim()
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

/**
 * Valide une adresse email
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/**
 * Valide un numéro de téléphone (français ou international)
 */
export function isValidPhone(phone: string): boolean {
  if (!phone || typeof phone !== 'string') return false;
  return /^[\d\s+()-]{6,20}$/.test(phone.trim());
}

/**
 * Transforme une valeur selon le type de champ
 */
export async function transformValue<T>(
  value: string,
  field: keyof T,
  config: CSVImportConfig<T>
): Promise<any> {
  // Valeur vide
  if (!value || value.trim() === '') {
    return null;
  }

  // Transformer personnalisé
  if (config.fieldTransforms?.[field]) {
    try {
      return config.fieldTransforms[field]!(value);
    } catch (error) {
      console.warn(`Erreur de transformation pour ${String(field)}:`, error);
      return value;
    }
  }

  // Résolution de relation
  if (config.relationResolvers?.[field]) {
    try {
      return await config.relationResolvers[field]!(value);
    } catch (error) {
      console.warn(`Erreur de résolution de relation pour ${String(field)}:`, error);
      return null;
    }
  }

  return value;
}

/**
 * Valide une ligne CSV
 */
export function validateCSVRow<T>(
  row: Record<string, any>,
  mapping: CSVColumnMapping[],
  config: CSVImportConfig<T>,
  lineNumber: number
): CSVValidationError[] {
  const errors: CSVValidationError[] = [];

  // Vérifier les champs obligatoires
  config.requiredFields.forEach((field) => {
    const fieldStr = String(field);
    const mappedColumn = mapping.find((m) => m.dbField === fieldStr);

    if (!mappedColumn) {
      errors.push({
        line: lineNumber,
        field: fieldStr,
        value: null,
        error: `Champ obligatoire non mappé`,
      });
      return;
    }

    const value = row[fieldStr];
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      errors.push({
        line: lineNumber,
        field: fieldStr,
        value: value,
        error: `Champ obligatoire vide`,
      });
    }
  });

  // Valider chaque champ selon les validateurs personnalisés
  Object.keys(row).forEach((field) => {
    const value = row[field];
    const validator = config.fieldValidations?.[field as keyof T];

    if (validator && value) {
      const result = validator(value);
      if (result !== true) {
        errors.push({
          line: lineNumber,
          field,
          value,
          error: typeof result === 'string' ? result : 'Valeur invalide',
        });
      }
    }
  });

  return errors;
}

/**
 * Transforme une ligne CSV en objet de données pour création/mise à jour
 */
export async function transformCSVRow<T>(
  csvRow: Record<string, string>,
  mapping: CSVColumnMapping[],
  config: CSVImportConfig<T>
): Promise<Partial<T>> {
  const transformed: Record<string, any> = {};

  for (const map of mapping) {
    if (!map.dbField || map.dbField === 'ignore') continue;

    const csvValue = csvRow[map.csvColumn];
    const transformedValue = await transformValue(csvValue, map.dbField as keyof T, config);

    transformed[map.dbField] = transformedValue;
  }

  return transformed as Partial<T>;
}

/**
 * Importe des données CSV avec validation et gestion d'erreurs
 * Import par batch de 50 lignes
 */
export async function importCSVData<T extends { id?: string }>(
  rows: Record<string, string>[],
  mapping: CSVColumnMapping[],
  config: CSVImportConfig<T>,
  mode: CSVImportMode,
  onCreate: (data: Partial<T>) => Promise<T | boolean | null>,
  onUpdate?: (id: string, data: Partial<T>) => Promise<boolean>,
  existingData?: T[],
  onProgress?: (current: number, total: number) => void
): Promise<CSVImportResult<T>> {
  const startTime = Date.now();
  let created = 0;
  let updated = 0;
  const errors: CSVValidationError[] = [];

  const BATCH_SIZE = 50;

  for (let i = 0; i < rows.length; i += BATCH_SIZE) {
    const batch = rows.slice(i, Math.min(i + BATCH_SIZE, rows.length));

    for (let j = 0; j < batch.length; j++) {
      const csvRow = batch[j];
      const lineNumber = i + j + 2; // +2 car header + index base 0

      try {
        // Transformer la ligne
        const transformedRow = await transformCSVRow(csvRow, mapping, config);

        // Appliquer les champs dérivés
        if (config.derivedFields) {
          for (const [field, deriveFn] of Object.entries(config.derivedFields)) {
            if (!transformedRow[field as keyof T]) {
              // Ne calculer que si le champ n'existe pas déjà
              transformedRow[field as keyof T] = deriveFn(transformedRow);
            }
          }
        }

        // Valider la ligne
        const validationErrors = validateCSVRow(transformedRow, mapping, config, lineNumber);

        if (validationErrors.length > 0) {
          errors.push(...validationErrors);
          continue; // Passer à la ligne suivante
        }

        // Mode upsert : chercher si l'élément existe déjà
        if (mode.mode === 'upsert' && mode.upsertKey && existingData) {
          const upsertValue = transformedRow[mode.upsertKey as keyof T];
          const existing = existingData.find(
            (item) => item[mode.upsertKey as keyof T] === upsertValue
          );

          if (existing && existing.id && onUpdate) {
            // Mise à jour
            try {
              const success = await onUpdate(existing.id, transformedRow);
              if (success) {
                updated++;
              } else {
                errors.push({
                  line: lineNumber,
                  field: mode.upsertKey,
                  value: upsertValue,
                  error: 'Échec de la mise à jour',
                });
              }
            } catch (updateError) {
              errors.push({
                line: lineNumber,
                field: mode.upsertKey,
                value: upsertValue,
                error: parseSupabaseError(updateError),
              });
            }
            continue;
          }
        }

        // Création
        try {
          const result = await onCreate(transformedRow);
          if (result) {
            created++;
          } else {
            errors.push({
              line: lineNumber,
              field: 'general',
              value: JSON.stringify(transformedRow),
              error: 'Échec de la création (raison inconnue)',
            });
          }
        } catch (createError) {
          // Capture spécifique des erreurs de création (duplication, contraintes, etc.)
          errors.push({
            line: lineNumber,
            field: 'general',
            value: JSON.stringify(transformedRow),
            error: parseSupabaseError(createError),
          });
        }
      } catch (error) {
        // Erreur générale (transformation, validation, etc.)
        errors.push({
          line: lineNumber,
          field: 'general',
          value: JSON.stringify(csvRow),
          error: error instanceof Error ? error.message : 'Erreur inconnue',
        });
      }

      // Notifier la progression
      if (onProgress) {
        onProgress(i + j + 1, rows.length);
      }
    }
  }

  const duration = Date.now() - startTime;

  return {
    success: errors.length === 0,
    created,
    updated,
    errors,
    duration,
  };
}

/**
 * Exporte les erreurs d'import en CSV pour correction
 */
export function exportErrorsToCSV(errors: CSVValidationError[], filename: string) {
  const csv = Papa.unparse(
    errors.map((err) => ({
      Ligne: err.line,
      Champ: err.field,
      Valeur: err.value,
      Erreur: err.error,
    })),
    {
      delimiter: ';',
      header: true,
      // Les valeurs viennent du fichier importé, déjà déséchappées par parseCSVFile
      escapeFormulae: CSV_FORMULA_PATTERN,
    }
  );

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
